// The cart builder's Professional services section: the Managed Store build
// fee, the Managed AI and Managed AWS retainers, and the On Call / In House
// care plans. Which line each one adds, and what stops the rep adding it.
//
// The services, their line templates and the margin floor's constants come
// from squish-backend (lib/professional-services-catalog.js
// internalProjection, served at /api/v2/internal/catalogs/professional-services
// and passed through /api/products/live as `professionalServices`). The server
// re-prices every line when the cart is saved and again when it is paid, and
// refuses one under the floor, so the checks here exist to show the rep the
// refusal while they type, not to enforce it. They use the server's own
// wording so the rep reads the same sentence either way.
//
// The Managed Store's monthly share of sales is not a cart line and is never
// shown here: the section carries the build fee only.

export const PS_LINE_PREFIX = 'ps:';

const whole = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// Served order: the order the backend lists them in.
export function serviceList(psCatalog) {
  return Object.values(psCatalog?.services || {});
}

// The cheapest whole-dollar monthly price that clears the margin floor on a
// monthly cost: the payment fee comes out of the price, then the margin, and
// the result is rounded UP (rounding down would sell under the floor).
// squish-backend lib/cost-floor.js floorRetailWholeDollar; the served floor
// carries worked examples so this copy is tested against that one.
export function floorDollars(floor, cost) {
  const c = Number(cost);
  if (!floor || !Number.isFinite(c) || c < 0) return null;
  const denom = 1 - floor.paymentPct - floor.minMarginPct;
  if (!(denom > 0)) return null;
  return Math.ceil((c + floor.paymentFixedDollars) / denom);
}

// What the rep typed, as a number, or null for an empty field.
function typedNumber(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace(/[$,\s]/g, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

// The price this service would charge, given what was typed (null when the
// typed price is not a price yet).
export function servicePrice(svc, { dollars } = {}) {
  if (!svc) return null;
  if (svc.pricing === 'fixed') return svc.monthlyDollars;
  const n = typedNumber(dollars);
  return Number.isInteger(n) ? n : null;
}

// Why this service cannot be added at these figures (null = addable). Same
// order and wording as the server's refusal (quoteLine): the price, then the
// cost, then the floor.
export function serviceRefusal(svc, { dollars, cost } = {}, floor) {
  if (!svc) return 'Pick a service';
  let price = svc.monthlyDollars;
  if (svc.pricing === 'typed') {
    const what = svc.billing === 'monthly' ? `${svc.label} monthly price` : `${svc.label} fee`;
    const n = typedNumber(dollars);
    if (n === null || Number.isNaN(n)) return `${what} is missing: type it in whole dollars.`;
    if (!Number.isInteger(n)) return `${what} must be whole dollars, got ${whole(n)}.`;
    if (n < svc.minDollars || n > svc.maxDollars) {
      return `${what} must be between ${whole(svc.minDollars)} and ${whole(svc.maxDollars)}, got ${whole(n)}.`;
    }
    price = n;
  }
  if (svc.costBasis) {
    const c = typedNumber(cost);
    if (c === null || Number.isNaN(c)) {
      return `${svc.label} needs what it costs us a month (type 0 when there is no cost) so the margin floor can be checked.`;
    }
    if (c < 0) return `${svc.label} monthly cost cannot be negative.`;
    if (c > svc.maxDollars) return `${svc.label} monthly cost of ${whole(c)} is over the ${whole(svc.maxDollars)} limit.`;
    if (Math.abs(Math.round(c * 100) - c * 100) > 1e-6) return `${svc.label} monthly cost must be dollars and cents, got ${c}.`;
    const minimum = floorDollars(floor, c);
    if (minimum == null) return 'Margin floor unavailable: reload the cart builder';
    if (price < minimum) {
      const fee = price * floor.paymentPct + floor.paymentFixedDollars;
      const pct = Math.round(((price - fee - c) / price) * 100 * 100) / 100;
      return `${svc.label} at ${whole(price)}/mo against a ${whole(c)}/mo cost is ${pct}% margin, `
        + `below the ${Math.round(floor.minMarginPct * 100)}% floor. The minimum is ${whole(minimum)}/mo.`;
    }
  }
  return null;
}

// The cart line for this service at these figures: the served template, the
// typed price under the service's amountField, the stated cost under its
// costField (internal: the server strips it from every customer view), and the
// price on unitPrice. Null when serviceRefusal would refuse it.
export function serviceLine(svc, figures = {}, floor, lineId) {
  if (serviceRefusal(svc, figures, floor)) return null;
  const metadata = { ...svc.line.metadata };
  const unitPrice = servicePrice(svc, figures);
  if (svc.pricing === 'typed') metadata[svc.amountField] = unitPrice;
  if (svc.costBasis) metadata[svc.costField] = Math.round(typedNumber(figures.cost) * 100) / 100;
  return { ...svc.line, metadata, unitPrice, lineId };
}

// A fresh lineId: several retainers can sit in one cart, each its own line.
export function nextServiceLineId(items, sku) {
  const taken = new Set((items || []).map((i) => i.lineId));
  let n = 1;
  while (taken.has(`${PS_LINE_PREFIX}${sku}:${n}`)) n += 1;
  return `${PS_LINE_PREFIX}${sku}:${n}`;
}

// Why the section cannot add anything right now (null = addable).
export function professionalServicesBlocker({ psCatalog, isInvoice }) {
  if (!psCatalog?.services || !psCatalog?.floor) return 'Professional services catalog unavailable';
  if (isInvoice) return 'Card checkout only: switch to Card to sell a service';
  return null;
}

// One line under the picker saying how the service bills. No percentage for
// the Managed Store: its share of sales is agreed outside the cart.
export function billingNote(svc) {
  if (!svc) return '';
  if (svc.billing === 'one-time') return 'One-time fee, charged in full when the cart is paid.';
  const what = svc.desc ? `${svc.desc}. ` : '';
  return `${what}Month 1 is charged when the cart is paid, then monthly on the same card. Needs the customer's own account to pay.`;
}
